import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp, TrendingDown, Calendar, Award, Target, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";

const Progress = () => {
  const improvements = [
    { metric: "Resting Heart Rate", change: -5, unit: "bpm", trend: "down", description: "Improved cardiovascular efficiency" },
    { metric: "Body Fat Percentage", change: -1.5, unit: "%", trend: "down", description: "Reduced body fat composition" },
    { metric: "VO2 Max", change: 2, unit: "ml/kg/min", trend: "up", description: "Enhanced aerobic fitness" },
    { metric: "Workout Consistency", change: 15, unit: "%", trend: "up", description: "Increased training frequency" },
  ];

  const milestones = [
    { date: "3 months ago", achievement: "Embarked on your wellness journey", icon: Target },
    { date: "2 months ago", achievement: "Completed a 30-day workout streak", icon: Zap },
    { date: "1 month ago", achievement: "Achieved target weight goal", icon: Award },
    { date: "Today", achievement: "Reached new personal best in squats", icon: "🔥" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Your Health & Fitness Journey</h1>
          <p className="text-muted-foreground">Visualize your achievements and track your transformation over time.</p>
        </div>

        {/* Stats Overview */}
        <div className="mb-8 animate-scale-in">
          <Card className="border shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl">Quarterly Health Improvements</CardTitle>
              <CardDescription>A summary of your key metric changes over the last 90 days.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {improvements.map((item, index) => (
                  <div key={index} className="p-4 rounded-xl bg-muted">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-muted-foreground">{item.metric}</span>
                      {item.trend === "down" ? (
                        <TrendingDown className="h-5 w-5 text-green-600" />
                      ) : (
                        <TrendingUp className="h-5 w-5 text-green-600" />
                      )}
                    </div>
                    <div className="text-2xl font-bold mb-1">
                      {item.change > 0 ? "+" : ""}{item.change} {item.unit}
                    </div>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Health Score Chart */}
        <div className="mb-8 animate-slide-up">
          <Card className="border shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl">Health Score Evolution</CardTitle>
              <CardDescription>Your AI-calculated health score over the last 6 months.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-end justify-between gap-2 px-2">
                {[65, 68, 72, 75, 78, 82].map((value, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center gap-2">
                    <div 
                      className="w-full rounded-t-lg bg-primary/80 transition-all hover:bg-primary"
                      style={{ height: `${value}%` }}
                    />
                    <span className="text-xs text-muted-foreground">Month {index + 1}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                <p className="text-sm font-medium text-green-600">
                  ↑ 17% - Incredible progress! Your health score has significantly improved over the last quarter.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Milestones */}
        <div className="animate-slide-up" style={{ animationDelay: "0.1s" }}>
          <h2 className="text-2xl font-semibold mb-4">Your Achievements</h2>
          <Card className="border shadow-sm">
            <CardContent className="pt-6">
              <div className="space-y-3">
                {milestones.map((milestone, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 rounded-xl bg-muted hover:bg-muted/80 transition-colors">
                    <div className="text-2xl">
                      {typeof milestone.icon === 'string' ? milestone.icon : <milestone.icon className="h-6 w-6 text-primary" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold">{milestone.achievement}</div>
                      <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        <span>{milestone.date}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Progress;
